/**
 * Loads and attaches NPC-body and world-object GLB models into an
 * already-built scene. Shared between the live game (base-view/index.ts) and
 * `ultra_map_editor`, so both use the exact same loading/disposal-safety
 * behaviour — what you place/see in the editor is guaranteed to be what
 * ships in the game, because it's not a second implementation that could
 * drift from the first.
 *
 * `isTornDown` is polled after each async load resolves so a load that
 * finishes after the caller's view has already been torn down doesn't leak a
 * mesh into a disposed scene (see scene.ts's `dispose()` for the matching
 * `sharedAsset` detach-not-dispose handling this relies on).
 */
import * as THREE from 'three';
import type { WorldData, ActorPose } from '../types';
import type { SceneHandles } from './scene';
import { tint } from './color-utils';
import { loadCharacterModel } from '../../character-select/load-model';
import { createNpcAnimator, type NpcAnimator } from './character-animations';
import { isWeaponModel, WEAPON_WORLD_POSE_FIXUPS, BUILDING_ROTATION_FIXUP } from './model-catalog';

/**
 * Clones each mesh's material and gives it a non-black emissive so the
 * hover-glow system (which just multiplies each mesh's existing `emissive`
 * by an intensity) has something to work with — GLB materials are almost
 * never authored with an emissive channel. Cloned per-mesh first — mutating
 * the shared/cached material would leak the tint into every other clone of
 * this GLB (and the cache itself, corrupting the next mount).
 *
 * `MeshStandardMaterial`'s `emissiveIntensity` defaults to 1 — without
 * explicitly zeroing it, the tint renders at full strength immediately (a
 * permanent near-white wash over the whole model), not gated behind hover
 * at all. `baseEmissiveIntensity` only restores this value on hover-EXIT; it
 * does nothing for a model that's never been hovered yet, which is why the
 * live material property has to start at 0 too, not just the stored
 * reference value.
 *
 * Exported (not just inlined in `attachWorldModels`) so `ultra_map_editor`'s
 * spawn-a-new-object flow can apply the identical fix to a single
 * dynamically-loaded model, not a second hand-copied version of it.
 */
export function applyHoverGlowFix(model: THREE.Object3D, color: number): void {
  model.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.geometry || Array.isArray(mesh.material)) return;
    const clone = (mesh.material as THREE.MeshStandardMaterial).clone();
    clone.emissive = new THREE.Color(tint(color, 1.7));
    clone.emissiveIntensity = 0;
    mesh.material = clone;
    mesh.userData.baseEmissiveIntensity = 0;
  });
}

/**
 * Weapons loose in the world (not held by a character — nothing holds one
 * yet) rest on the ground, not balanced upright the way a couple of the GLBs
 * were authored. `loadCharacterModel` has already scaled/bottom-aligned the
 * model against its AS-AUTHORED bounding box by the time this runs, so
 * rotating it (for the ones that need it) can lift its lowest point off
 * y = 0 or bury it — the box is re-measured and the model re-grounded after,
 * exactly as the initial load did before the rotation existed. A no-op for
 * every weapon not listed in `WEAPON_WORLD_POSE_FIXUPS`, and for anything
 * that isn't a weapon at all.
 *
 * Exported so `ultra_map_editor`'s spawn-a-new-object flow — which loads its
 * own preview model directly, not through `attachWorldModels` — applies the
 * identical correction rather than a second hand-copied version of it.
 */
export function applyWeaponWorldPose(model: THREE.Object3D, modelUrl: string): void {
  if (!isWeaponModel(modelUrl)) return;
  const fixup = WEAPON_WORLD_POSE_FIXUPS[modelUrl];
  if (!fixup) return;
  model.rotation.x = fixup.x ?? 0;
  model.rotation.z = fixup.z ?? 0;
  const box = new THREE.Box3().setFromObject(model);
  model.position.y -= box.min.y;
}

/**
 * Corrects a building GLB whose raw export orientation disagrees with the
 * rest of its level/variant family — see `BUILDING_ROTATION_FIXUP`'s doc
 * comment for which one(s) and why. A no-op for every model not listed
 * there. Pure Y-axis rotation only, unlike `applyWeaponWorldPose` — never
 * needs re-grounding afterward, since a Y rotation can't move a model's
 * lowest point.
 */
export function applyBuildingRotationFixup(model: THREE.Object3D, modelUrl: string): void {
  const fixup = BUILDING_ROTATION_FIXUP[modelUrl];
  if (fixup === undefined) return;
  model.rotation.y += fixup;
}

/**
 * Replaces the GLB hanging off an already-placed object root.
 *
 * The player's home is six meshes, one per upgrade level, and the settlement
 * has to show the one the player has actually paid for. `attachWorldModels`
 * reads `modelUrl` exactly once at mount, and the level arrives from the
 * server after that, so the swap has to happen on the live scene node.
 *
 * ─── What it deliberately does NOT touch ────────────────────────────────────
 * `size`, `collisionW` and `collisionD` on the map object. The home is placed
 * by its LARGEST mesh so that mesh does not clip its neighbours, and the
 * reserved footprint is meant to stay that size at every level — a smaller
 * house keeps the same plot. `collisionSize()` in `sim/base-sim.ts` reads
 * `collisionW ?? size.w`, so leaving both alone is what preserves that.
 * Picking and hover need no help either: `render/picking.ts` raycasts the real
 * geometry, so the click target follows the new mesh for free.
 *
 * It also does not touch `world` — the map module is a singleton that outlives
 * an SPA remount (see `scene.ts`'s dispose notes), so writing a level into it
 * would leak one player's home into the next mount and into the editor.
 *
 * ─── Disposal: materials only ───────────────────────────────────────────────
 * `loadCharacterModel` hands back `cached.clone()`, which SHARES geometry and
 * textures with the cached original; `applyHoverGlowFix` then swaps in a
 * per-mesh material clone. So the material clones are ours to free and nothing
 * else is. Disposing geometry or the texture maps here would corrupt every
 * other clone of that URL — which is the same reason `scene.ts`'s teardown
 * detaches `sharedAsset` subtrees before its generic dispose traversal, and
 * why `disposeModel` (load-model.ts) is the wrong tool for this.
 */
export function swapObjectModel(
  root: THREE.Object3D,
  modelUrl: string,
  targetHeight: number,
  color: number,
  isTornDown: () => boolean = () => false,
): Promise<void> {
  // Collected before mutating: `children` is live, so removing while iterating
  // it skips siblings — the same trap `scene.ts` documents for its traversal.
  for (const child of [...root.children]) {
    if (!child.userData.sharedAsset) continue;
    child.traverse((o) => {
      const mesh = o as THREE.Mesh;
      const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (!mat) return;
      for (const m of Array.isArray(mat) ? mat : [mat]) m.dispose();
    });
    child.removeFromParent();
  }

  return loadCharacterModel(modelUrl, targetHeight).then((model) => {
    if (isTornDown()) return;
    model.userData.sharedAsset = true;
    applyBuildingRotationFixup(model, modelUrl);
    applyHoverGlowFix(model, color);
    root.add(model);
  });
}

/**
 * Ambient NPC animators created by `attachWorldModels`, for the caller's
 * frame loop to drive. Returned rather than self-driving because the loading
 * is async and the frame loop lives in the view (`index.ts`): there is no
 * `requestAnimationFrame` in this module and adding one would give the
 * settlement a second, unsynchronized clock.
 *
 * `ultra_map_editor` ignores the return value — a static editor preview needs
 * no animation — which is safe because every animator no-ops on an unrigged
 * body anyway (see `createNpcAnimator`).
 */
export interface NpcAnimators {
  /** Advances every NPC animator. `talkingNpcId` is the NPC the player currently has dialog open with, if any. */
  update(dt: number, talkingNpcId: string | null): void;
  /**
   * Pushes the sim's live self-moving NPC poses onto their meshes — ambient
   * `wanderRadius` citizens and scripted `routine` NPCs alike.
   *
   * Every other `WorldObject` gets its transform written once at attach time;
   * these are the ones that keep moving, so their roots have to be re-written
   * each frame. Also drives each body's ACTIVITY (walk -> sit -> work) and its
   * visibility, which is what makes a routine's "goes inside to report" step
   * actually disappear.
   */
  syncActors(poses: readonly ActorPose[]): void;
  /** Fire a one-shot clip on one placed body — see the implementation's note. */
  playOneShot(objectId: string, kind: 'attack' | 'hitReact'): void;
  dispose(): void;
}

export function attachWorldModels(world: WorldData, sceneHandles: SceneHandles, isTornDown: () => boolean): NpcAnimators {
  /** Keyed by NPC id so the talking override can find the right one. */
  const npcAnimators = new Map<string, NpcAnimator>();
  /**
   * The same animators, keyed by OBJECT id.
   *
   * Two maps because the two consumers key differently and neither key is
   * derivable from the other here: the talking override knows an `npcId`,
   * while `syncActors` receives `ActorPose.objectId` (an ambient citizen is
   * deliberately npc-less, so object id is the only id it has). Looking
   * actors up in the npcId-keyed map silently misses every routine NPC that
   * DOES have an npcId — no error, the body just never changes activity.
   */
  const animatorsByObjectId = new Map<string, NpcAnimator>();

  for (const npc of Object.values(world.npcs)) {
    if (!npc.bodyModelUrl || !npc.bodyPosition) continue;
    const bodyPosition = npc.bodyPosition;
    void loadCharacterModel(npc.bodyModelUrl).then((model) => {
      if (isTornDown()) return;
      model.position.set(bodyPosition.x, 0, bodyPosition.z);
      model.rotation.y = npc.bodyFacing ?? 0;
      // Cache-owned clone (see scene.ts's dispose()) — flagged so teardown
      // detaches it instead of disposing its (shared) geometry/material.
      model.userData.sharedAsset = true;
      sceneHandles.scene.add(model);
    }).catch((err) => console.error(`[base-view] failed to load NPC body for "${npc.id}"`, err));
  }

  for (const obj of world.objects) {
    if (!obj.modelUrl) continue;
    const root = sceneHandles.objectMeshes.get(obj.id);
    if (!root) continue;
    const modelUrl = obj.modelUrl;
    void loadCharacterModel(modelUrl, obj.modelHeight ?? obj.size.h).then((model) => {
      if (isTornDown()) return;
      model.userData.sharedAsset = true;
      applyWeaponWorldPose(model, modelUrl);
      applyBuildingRotationFixup(model, modelUrl);
      applyHoverGlowFix(model, obj.color);
      root.add(model);
      // NPC bodies are placed as `kind: 'npc'` OBJECTS (this loop), not via
      // the `world.npcs` bodyModelUrl path above — that field is unset for
      // every settlement NPC. Attaching the animator here is therefore the
      // branch that actually runs. No-ops unless the GLB is rigged.
      if (obj.kind === 'npc') {
        // Keyed by npcId where there is one (the talking override looks the
        // animator up by it), else by object id: an ambient wandering citizen
        // is deliberately npc-less — it has no dialog and never talks — and
        // keying it here is what gets it animated at all.
        const key = obj.npcId ?? obj.id;
        const animator = createNpcAnimator(model, obj.npcActivity ?? 'stand', key, obj.heldItemKey, obj.equippedKeys);
        // A duplicate npcId would otherwise silently drop the first
        // animator's dispose(); last-one-wins is still wrong, so keep both by
        // disposing the one being replaced.
        npcAnimators.get(key)?.dispose();
        npcAnimators.set(key, animator);
        animatorsByObjectId.set(obj.id, animator);
      }
    }).catch((err) => console.error(`[base-view] failed to load model for "${obj.id}"`, err));
  }

  return {
    update(dt, talkingNpcId) {
      for (const [npcId, animator] of npcAnimators) {
        animator.update(dt, npcId === talkingNpcId);
      }
    },
    syncActors(poses) {
      for (const pose of poses) {
        // Missing root = this body's model hasn't finished loading (or the
        // object carries no modelUrl at all) — skip rather than throw, same
        // tolerance the attach loop above already has.
        const root = sceneHandles.objectMeshes.get(pose.objectId);
        if (!root) continue;
        root.position.x = pose.position.x;
        root.position.z = pose.position.z;
        root.rotation.y = pose.facing;
        // `hidden` is a routine step's "went inside" — hide the mesh AND stop
        // offering it as an interactable, or the player could talk to someone
        // who is visibly not there.
        root.visible = !pose.hidden;
        root.userData.hiddenActor = pose.hidden;
        // setActivity() no-ops when unchanged, so this is safe every frame.
        animatorsByObjectId.get(pose.objectId)?.setActivity(pose.activity);
      }
    },
    /**
     * Fire a one-shot clip on one placed body. `playAttack`/`playHitReact` are
     * one-shots, so unlike every `NpcActivity` they cannot be shown by static
     * map data — something has to call them. `test_dummies` does, on a timer
     * (see `index.ts`); nothing in normal play uses this yet.
     */
    playOneShot(objectId, kind) {
      const animator = animatorsByObjectId.get(objectId);
      if (!animator) return;
      if (kind === 'attack') animator.playAttack();
      else animator.playHitReact();
    },
    dispose() {
      for (const animator of npcAnimators.values()) animator.dispose();
      npcAnimators.clear();
      // Same animator objects as above (already disposed) — just drop the refs.
      animatorsByObjectId.clear();
    },
  };
}
